import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Table, Tag, Button, Space, Tooltip, App, Card, Segmented, Typography, type TableColumnsType } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, ArrowLeftOutlined, HolderOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { useDisplaySections, setSectionTopics } from '@/api/displaySections';
import { useTopics, TOPICS_KEY } from '@/hooks/useTopics';
import { useLanguages } from '@/hooks/useLanguages';
import { useTopicTypes } from '@/api/topicTypes';
import { useConfirmDeleteTopic } from '@/pages/Topics/DeleteTopicConfirm';
import { topicsInSection, CONTENT_TYPE_NAME } from './contentModel';
import type { Topic } from '@/types/topic';

export function SectionDetailPage() {
  const { sectionId } = useParams<{ sectionId: string }>();
  const id = Number(sectionId);
  const nav = useNavigate();
  const { message } = App.useApp();
  const qc = useQueryClient();
  const secQ = useDisplaySections();
  const topicsQ = useTopics({});
  const typesQ = useTopicTypes();
  const langsQ = useLanguages();
  const confirmDel = useConfirmDeleteTopic();
  const [dragKey, setDragKey] = useState<number | null>(null);
  const [lang, setLang] = useState('en');

  const section = (secQ.data ?? []).find((s) => s.id === id);
  const typeName = (tid: number) =>
    typesQ.data?.find((t) => t.id === tid)?.name ?? CONTENT_TYPE_NAME[tid] ?? `type ${tid}`;
  const enabledLangs = (langsQ.data ?? []).filter((l) => l.enabled).map((l) => l.code);
  const langOptions = (enabledLangs.length ? enabledLangs : ['en']).map((c) => ({ label: c, value: c }));
  // Languages a topic has content for (title keys, minus the __structured__ blob).
  const langsOf = (t: Topic): string[] =>
    Object.keys(t.details.title ?? {}).filter((k) => k !== '__structured__');

  // The topic's sort_index within THIS section (ascending = app order).
  const sortFor = (t: Topic) =>
    (t.display_sections ?? []).find((s) => s.id === id)?.sort_index ?? Number.MAX_SAFE_INTEGER;
  const rows = [...topicsInSection(topicsQ.data ?? [], id)].sort((a, b) => sortFor(a) - sortFor(b));

  async function reorder(orderedIds: number[]) {
    try {
      await setSectionTopics(id, orderedIds);
      qc.invalidateQueries({ queryKey: TOPICS_KEY });
    } catch (e) {
      message.error((e as Error).message);
    }
  }

  const columns: TableColumnsType<Topic> = [
    { title: '', key: 'drag', width: 30, render: () => <HolderOutlined style={{ cursor: 'grab', color: '#999' }} /> },
    {
      title: `Title (${lang})`,
      ellipsis: true,
      render: (_, t) => {
        const val = t.details.title?.[lang];
        if (val) return val;
        const en = t.details.title?.en;
        // Fall back to English (muted) when the selected language is missing.
        return <span style={{ color: '#999', fontStyle: 'italic' }}>{en ? `${en} (en)` : '(untitled)'}</span>;
      },
    },
    { title: 'Type', width: 110, render: (_, t) => typeName(t.type_id) },
    {
      title: 'Languages', width: 190,
      render: (_, t) => {
        const ls = langsOf(t);
        return ls.length ? (
          <Space size={4} wrap>
            {ls.map((l) => (
              <Tag key={l} color={l === lang ? 'blue' : 'default'} style={{ marginInlineEnd: 0 }}>{l}</Tag>
            ))}
          </Space>
        ) : <span style={{ color: '#999' }}>—</span>;
      },
    },
    {
      title: 'Status', width: 100,
      render: (_, t) => <Tag color={t.status === 'active' ? 'success' : 'default'}>{t.status}</Tag>,
    },
    {
      title: 'Updated', width: 140,
      render: (_, t) => {
        const d = t.edited_at ? new Date(t.edited_at) : null;
        return d && !Number.isNaN(d.getTime()) ? formatDistanceToNow(d, { addSuffix: true }) : '—';
      },
    },
    {
      title: 'Actions', width: 110,
      render: (_, t) => (
        <Space>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => nav(`/topics/${t.id}?section=${id}`)} />
          </Tooltip>
          <Tooltip title="Delete">
            <Button size="small" danger icon={<DeleteOutlined />}
              onClick={async () => { if (await confirmDel(t)) message.success('Removed'); }} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1100 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => nav('/content')}>Content Center</Button>
        <b style={{ fontSize: 18 }}>{section?.tag_name ?? `Section ${id}`}</b>
      </Space>
      <Card
        title={`${rows.length} topic(s) · drag to reorder — sets the order shown in the app`}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => nav(`/topics/new?section=${id}`)}>
            Add
          </Button>
        }
      >
        <Space style={{ marginBottom: 12 }} align="center">
          <Typography.Text type="secondary">Language</Typography.Text>
          <Segmented size="small" value={lang} onChange={(v) => setLang(v as string)} options={langOptions} />
        </Space>
        <Table<Topic>
          rowKey="id"
          size="small"
          loading={topicsQ.isLoading}
          dataSource={rows}
          pagination={false}
          columns={columns}
          onRow={(record, index) => ({
            draggable: true,
            onDragStart: () => setDragKey(Number(record.id)),
            onDragOver: (e) => e.preventDefault(),
            onDrop: () => {
              const key = Number(record.id);
              if (dragKey != null && dragKey !== key && index !== undefined) {
                const ids = rows.map((t) => Number(t.id)).filter((x) => x !== dragKey);
                ids.splice(index, 0, dragKey);
                reorder(ids);
              }
              setDragKey(null);
            },
          })}
        />
      </Card>
    </div>
  );
}
